import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useNavigate } from 'react-router-dom'
import { Folder } from '../../types/models'

const ItemType = 'FOLDER'

interface DragItem {
  id: string
  index: number
}

interface DraggableFolderItemProps {
  folder: Folder
  index: number
  selectedFolderId?: string | null
  onFolderSelect: (folderId: string | null) => void
  onMove: (dragIndex: number, hoverIndex: number) => void
  onDrop?: (dragIndex: number, hoverIndex: number) => void
  children: React.ReactNode
}

export function DraggableFolderItem({
  folder,
  index,
  selectedFolderId,
  onFolderSelect,
  onMove,
  onDrop,
  children
}: DraggableFolderItemProps) {
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const originalIndexRef = useRef<number>(index)

  const [{ handlerId }, drop] = useDrop({
    accept: ItemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // 同じ要素の場合は何もしない
      if (dragIndex === hoverIndex) {
        return
      }

      // 画面上の位置を取得
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      
      // 垂直方向の中点を取得
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      
      // マウスの位置を取得
      const clientOffset = monitor.getClientOffset()
      
      // マウスの垂直位置を取得
      const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top

      // 下向きにドラッグしている場合、カーソルが中点を超えた時のみ移動
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // 上向きにドラッグしている場合、カーソルが中点を下回った時のみ移動
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // 実際に移動を実行
      onMove(dragIndex, hoverIndex)

      // Note: ここでitemのindexを更新することで、
      // 次のhover呼び出し時に再び同じ結果になることを防ぐ
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemType,
    item: () => {
      originalIndexRef.current = index
      return { id: folder.id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const didDrop = monitor.didDrop()
      console.log('🏁 Drag ended:', { didDrop, originalIndex: originalIndexRef.current, currentIndex: item.index, hasOnDrop: !!onDrop })
      if (didDrop && onDrop) {
        console.log('💾 Calling onDrop with:', { originalIndex: originalIndexRef.current, currentIndex: item.index })
        // ドロップが成功した場合、元のインデックスと現在のインデックスを使って保存
        onDrop(originalIndexRef.current, item.index)
      }
    },
  })

  const opacity = isDragging ? 0.4 : 1
  
  // ドラッグハンドルとドロップターゲットを設定
  drag(drop(ref))

  return (
    <div
      ref={ref}
      style={{ opacity, paddingTop: 4, paddingBottom: 4, minHeight: 0 }}
      data-handler-id={handlerId}
      className={`nav-item relative cursor-pointer group ${
        selectedFolderId === folder.id ? 'active' : ''
      }`}
      onClick={() => {
        navigate('/documents')
        onFolderSelect(folder.id)
      }}
    >
      {/* ドラッグハンドル */}
      <div className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing transition-opacity text-tertiary">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M4 8h16M4 16h16" />
        </svg>
      </div>
      
      {children}
    </div>
  )
}
